import { FirestoreCollections, ID, NewTask } from '@griply/types';

import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { buildTaskDocument } from './build-task-document';

export type CreateTasksOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TTimestamp = unknown,
  TDb = FirestoreInstance,
> = {
  tasks: NewTask[];
  userId: ID;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: ID };
  setDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<ID, unknown>,
  ) => Promise<void> | void;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  timestampFromDate: (date: Date) => TTimestamp;
  writeBatch?: (db: NoInfer<TDb>) => {
    set: (ref: NoInfer<TDocRef>, data: Record<ID, unknown>) => void;
    commit: () => Promise<void>;
  };
  onWriteError: (error: unknown) => void;
};

export const createTasks = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TTimestamp,
  TDb = FirestoreInstance,
>(
  options: CreateTasksOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TTimestamp,
    TDb
  >,
): Promise<ID[]> => {
  const {
    tasks,
    userId,
    collection,
    doc,
    setDoc,
    db,
    serverTimestamp,
    timestampFromDate,
    writeBatch,
    onWriteError,
  } = options;

  const taskCollection = collection(db, FirestoreCollections.Tasks);
  const ids: ID[] = [];

  if (tasks.length === 1 || !writeBatch) {
    tasks.forEach((task) => {
      const docRef = doc(taskCollection);
      ids.push(docRef.id);
      const data = buildTaskDocument(
        task,
        docRef,
        userId,
        serverTimestamp,
        timestampFromDate,
        task.parentIds ?? null,
      );
      detachWrite(setDoc(docRef, data), onWriteError);
    });
    return ids;
  }

  const batch = writeBatch(db);
  tasks.forEach((task) => {
    const docRef = doc(taskCollection);
    ids.push(docRef.id);
    const data = buildTaskDocument(
      task,
      docRef,
      userId,
      serverTimestamp,
      timestampFromDate,
      task.parentIds ?? null,
    );
    batch.set(docRef, data);
  });
  detachWrite(batch.commit(), onWriteError);
  return ids;
};
